import { lineSimilarity } from '../tracks/sectionMarkers';

/**
 * Lyric lines that follow the track (2026-09-30, pass 3 D4 remainder).
 * Anthony: highlight the line under the playhead, and "click on a lyric and
 * have the playhead move to that lyric, or just before that lyric, just like
 * Apple Music does on the iPhone."
 *
 * The Lyrics pane shows the SONG's text (repertoire lyrics, with section
 * labels); the times live on the playing FILE (`lyrics.timed`, seconds from
 * the start of that file). Same join as the section markers
 * (tracks/sectionMarkers.js): by the words, IN ORDER, so a chorus sung three
 * times takes its first, second and third timing in turn.
 *
 * Two rules that keep it honest:
 * - A line whose words are not found gets NO time, never a neighbour's: it
 *   is neither highlighted nor tappable. A tap that lands in the wrong verse
 *   is worse than a line that does nothing.
 * - The search looks a short way ahead of the last match (WINDOW), not to
 *   the end of the song. Lyrics sites carry lines a recording skips; an
 *   unbounded search would let one such line match a LATER repeat of the
 *   chorus and skip every line in between.
 */

const MATCH_AT = 0.6;
const WINDOW = 8;

/** Start a tapped line this many seconds early, so the singer hears the lead-in. */
export const LEAD_SECONDS = 0.5;

/** One entry per line of `lines`: its start in the track, or null. */
export const lineTimes = (lines = [], timed = []) => {
	const out = (Array.isArray(lines) ? lines : []).map(() => null);
	if (!Array.isArray(timed) || !timed.length) return out;
	let cursor = 0;
	out.forEach((_, i) => {
		const text = String(lines[i] || '');
		if (!text.trim()) return;
		const stop = Math.min(timed.length, cursor + WINDOW);
		for (let j = cursor; j < stop; j += 1) {
			if (lineSimilarity(text, timed[j] && timed[j].text) >= MATCH_AT) {
				const t = Number(timed[j].t);
				if (Number.isFinite(t) && t >= 0) out[i] = t;
				cursor = j + 1;
				break;
			}
		}
	});
	return out;
};

/** The line being sung at `t`: the latest timed line at or before it, or -1
 *  (the intro, before the first line). */
export const activeLineIndex = (times = [], t = 0) => {
	let best = -1;
	let bestT = -Infinity;
	times.forEach((lt, i) => {
		if (lt !== null && lt <= t + 0.05 && lt >= bestT) {
			best = i;
			bestT = lt;
		}
	});
	return best;
};

/** Where a tap on a line sends the playhead. */
export const seekTargetFor = (lineT) => Math.max(0, (Number(lineT) || 0) - LEAD_SECONDS);

// Which file a tap starts when none of this song's tracks is open: the backing
// Track (what a student sings over), then the Reference, then anything else,
// the Vocals stem last. Lower is better.
const VARIANT_RANK = (v) => {
	const k = String(v || 'Track').trim().toLowerCase();
	if (k === 'track') return 0;
	if (k === 'reference') return 1;
	if (k === 'vocals') return 3;
	return 2;
};

/**
 * The file a lyric tap should start, or null: one this page can play
 * (`playable`) that carries timed words (`timedOf`). Ties keep the list's
 * order (the Tracks pane's order).
 */
export const pickLyricTrack = (files = [], { timedOf, playable }) => {
	const ok = (Array.isArray(files) ? files : []).filter((f) => f && playable(f) && timedOf(f));
	if (!ok.length) return null;
	return ok.reduce((best, f) => (VARIANT_RANK(f.variant) < VARIANT_RANK(best.variant) ? f : best), ok[0]);
};
